import { useQuery } from '@tanstack/react-query';
import { supabaseHarvest } from '@/lib/supabase';
import { describeJob } from '@/lib/hangfire-format';
import type {
  ActiveWorkerJob,
  HangfireJobRow,
  HangfireServerRow,
  JobStateCount,
} from '@/types/hangfire';

/**
 * Friendly job label from Hangfire invocationdata (jsonb or legacy string).
 * For the Presto jobs that dispatch on an enum argument this appends the target
 * (e.g. "PrestoHarvestJob — Event Team Stats"); see lib/hangfire-format.
 */
function jobTypeName(invocationdata: { Type?: string } | string | null): string {
  return describeJob(invocationdata) ?? 'Unknown job';
}

/** Server label: "revo-presto-ip-10-0-1-7:7:guid" -> "revo-presto". */
export function shortenServerId(serverId: string | null | undefined): string {
  if (!serverId) return 'unknown';
  return serverId.split(':')[0].replace(/-ip-.*$/, '');
}

/**
 * Hangfire (PostgreSql storage) tables live in the `harvest` schema, lowercased.
 * The names below are the defaults emitted by Hangfire.PostgreSql v1.x.
 */
// Only the tables the Admin UI actually reads are listed here. Hangfire
// stores recurring-job metadata in a `set` table with key 'recurring-jobs'
// and pending work in `jobqueue`; add them back here if a future page needs
// them — the prior version listed several names that were never queried.
const HF_TABLES = {
  job: 'job',
  servers: 'server',
  // Per-transition state rows. The Succeeded state's `data` jsonb carries
  // PerformanceDuration (execution ms) + Latency, which we surface as a job
  // duration in the recent-jobs grid.
  state: 'state',
} as const;

/**
 * Hard cap on the per-state job scan. Exposed so consumers can label it in
 * a truncation warning banner. Long-term, replace the client-side scan with
 * a server-side rollup view / RPC.
 */
export const JOB_STATE_COUNTS_ROW_CAP = 20_000;

/**
 * Per-state counts of jobs over the last `windowHours` hours.
 *
 * Returns `{ counts, truncated }` so the UI can warn when the underlying
 * row scan hit the JOB_STATE_COUNTS_ROW_CAP cap — without that signal,
 * client-side aggregation silently undercounts on busy systems. The right
 * long-term fix is a SQL aggregation view / RPC; this is the near-term
 * mitigation.
 */
export function useJobStateCounts(windowHours = 24) {
  return useQuery({
    queryKey: ['hf-job-state-counts', windowHours],
    // 30s cache so tab-focus re-renders don't refetch up to 20k rows. The
    // state-distribution panel tolerates 30s of staleness comfortably.
    staleTime: 30_000,
    queryFn: async (): Promise<{ counts: JobStateCount[]; truncated: boolean }> => {
      const since = new Date(Date.now() - windowHours * 3600_000).toISOString();
      const { data, error } = await supabaseHarvest
        .from(HF_TABLES.job)
        .select('statename')
        .gte('createdat', since)
        .limit(JOB_STATE_COUNTS_ROW_CAP);
      if (error) throw error;
      const counts = new Map<string, number>();
      for (const r of data ?? []) {
        const state = (r as { statename: string | null }).statename ?? 'Unknown';
        counts.set(state, (counts.get(state) ?? 0) + 1);
      }
      const sorted = [...counts.entries()]
        .map(([state, count]) => ({ state, count }))
        .sort((a, b) => b.count - a.count);
      // The cap may be exactly the population size — treat ==cap as the
      // "possibly truncated" signal so we err on the side of warning rather
      // than silently undercounting.
      return { counts: sorted, truncated: (data?.length ?? 0) >= JOB_STATE_COUNTS_ROW_CAP };
    },
  });
}


/** Recent jobs, optionally filtered by state. */
export function useRecentJobs(opts: { state?: string; limit?: number } = {}) {
  const { state, limit = 100 } = opts;
  return useQuery({
    queryKey: ['hf-recent-jobs', state, limit],
    queryFn: async (): Promise<HangfireJobRow[]> => {
      let q = supabaseHarvest
        .from(HF_TABLES.job)
        .select('id, statename, invocationdata, createdat, expireat')
        .order('createdat', { ascending: false })
        .limit(limit);
      if (state) q = q.eq('statename', state);
      const { data, error } = await q;
      if (error) throw error;
      const jobs = (data ?? []) as HangfireJobRow[];
      return enrichJobs(jobs);
    },
  });
}

/**
 * Jobs currently held by a worker. Built from `Processing` jobs joined to their
 * Processing-state data (ServerId / WorkerId / StartedAt) so the UI can show
 * which worker is running what and for how long. Polls every 10s.
 */
export function useActiveWorkerJobs() {
  return useQuery({
    queryKey: ['hf-active-workers'],
    refetchInterval: 10_000,
    queryFn: async (): Promise<ActiveWorkerJob[]> => {
      const { data, error } = await supabaseHarvest
        .from(HF_TABLES.job)
        .select('id, invocationdata, createdat')
        .eq('statename', 'Processing')
        .order('createdat', { ascending: true })
        .limit(500);
      if (error) throw error;
      const jobs = (data ?? []) as { id: number; invocationdata: { Type?: string } | string | null; createdat: string }[];
      if (jobs.length === 0) return [];

      const serverByJob = await fetchProcessingState(jobs.map((j) => j.id));
      const now = Date.now();
      return jobs
        .map((j) => {
          const st = serverByJob.get(j.id);
          const startedMs = st?.StartedAt ? Number(st.StartedAt) : null;
          return {
            id: j.id,
            jobType: jobTypeName(j.invocationdata),
            serverId: st?.ServerId ?? 'unknown',
            workerId: st?.WorkerId ?? '',
            startedAt: startedMs ? new Date(startedMs).toISOString() : null,
            ageSeconds: Math.max(
              0,
              Math.round((now - (startedMs ?? new Date(j.createdat).getTime())) / 1000),
            ),
          } satisfies ActiveWorkerJob;
        })
        .sort((a, b) => b.ageSeconds - a.ageSeconds);
    },
  });
}

interface ProcessingStateData {
  ServerId?: string;
  WorkerId?: string;
  StartedAt?: string;
}

/** Map jobid -> latest Processing-state data for the given job ids. */
async function fetchProcessingState(jobIds: number[]): Promise<Map<number, ProcessingStateData>> {
  const byJob = new Map<number, ProcessingStateData>();
  if (jobIds.length === 0) return byJob;
  const { data, error } = await supabaseHarvest
    .from(HF_TABLES.state)
    .select('jobid, data')
    .eq('name', 'Processing')
    .in('jobid', jobIds);
  if (error || !data) return byJob;
  for (const row of data as { jobid: number; data: ProcessingStateData | null }[]) {
    // A retried job can have multiple Processing states; last write wins (the
    // most recent run's worker), which is what "who's running this" should show.
    if (row.data) byJob.set(row.jobid, row.data);
  }
  return byJob;
}

/**
 * Enrich job rows from the Hangfire `state` table (no FK from job.stateid, so we
 * join client-side by jobid). Two enrichments, best-effort:
 *  - durationMs: Succeeded state's `data.PerformanceDuration` (ms).
 *  - serverId:   Processing state's `data.ServerId` (which worker ran it),
 *                so the recent-jobs grid can show + filter by worker.
 * If the `state` table isn't readable the grid still renders unenriched.
 */
async function enrichJobs(jobs: HangfireJobRow[]): Promise<HangfireJobRow[]> {
  const ids = jobs.map((j) => j.id);
  if (ids.length === 0) return jobs;

  const { data, error } = await supabaseHarvest
    .from(HF_TABLES.state)
    .select('jobid, name, data')
    .in('name', ['Succeeded', 'Processing'])
    .in('jobid', ids);
  if (error || !data) return jobs;

  const durationByJob = new Map<number, number>();
  const serverByJob = new Map<number, string>();
  for (const row of data as {
    jobid: number;
    name: string;
    data: { PerformanceDuration?: string | number; ServerId?: string } | null;
  }[]) {
    if (row.name === 'Succeeded') {
      const raw = row.data?.PerformanceDuration;
      const ms = typeof raw === 'string' ? Number(raw) : raw;
      // Largest wins across retries (a job normally has exactly one Succeeded).
      if (ms != null && Number.isFinite(ms)) {
        durationByJob.set(row.jobid, Math.max(durationByJob.get(row.jobid) ?? 0, ms));
      }
    } else if (row.name === 'Processing' && row.data?.ServerId) {
      serverByJob.set(row.jobid, row.data.ServerId);
    }
  }

  return jobs.map((j) => ({
    ...j,
    durationMs: durationByJob.get(j.id) ?? j.durationMs,
    serverId: serverByJob.get(j.id) ?? null,
  }));
}

/** Heartbeat from each active Hangfire server. */
export function useHangfireServers() {
  return useQuery({
    queryKey: ['hf-servers'],
    queryFn: async (): Promise<HangfireServerRow[]> => {
      const { data, error } = await supabaseHarvest
        .from(HF_TABLES.servers)
        .select('id, data, lastheartbeat')
        .order('lastheartbeat', { ascending: false });
      if (error) throw error;
      return (data ?? []) as HangfireServerRow[];
    },
    refetchInterval: 15_000,
  });
}

// useRecurringJobs() was removed: the cron-management surface lives in
// CronJobsRoute, which composes JOB_DEFAULTS + harvest.cron_schedule_overrides
// instead of reading Hangfire's recurring-jobs set. If a future page needs
// Hangfire's view of recurring jobs, add `set: 'set'` back to HF_TABLES and
// SELECT against it with `.eq('key', 'recurring-jobs')`.
